/**
 * EventListItem - Single event display in the events list
 * Extracted from EventsTab.jsx
 */
import { Link } from "react-router-dom";
import { 
  Calendar, MapPin, Trophy, Edit, Trash2, Eye, EyeOff, 
  ChevronDown, ChevronUp, GitBranch 
} from "lucide-react";

export const EventListItem = ({ 
  event, 
  isExpanded, 
  onToggleExpand, 
  onEdit, 
  onDelete, 
  onTogglePublish 
}) => (
  <div className="bg-zinc-900/50 border border-white/10 overflow-hidden">
    {/* Event Header */}
    <div 
      className="flex items-center gap-4 p-4 cursor-pointer hover:bg-white/5"
      onClick={() => onToggleExpand(event.id)}
    >
      {/* Event Logo/Color */}
      <div 
        className="w-16 h-16 flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: event.color + '20' }}
      >
        {event.logo_url ? (
          <img src={event.logo_url} alt={event.name} className="w-12 h-12 object-contain" />
        ) : (
          <Trophy className="w-8 h-8" style={{ color: event.color }} />
        )}
      </div>

      {/* Event Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="font-secondary text-lg font-bold text-white truncate">{event.name}</h3>
          <span className={`px-2 py-0.5 text-[10px] uppercase ${
            event.event_type === 'all_star' 
              ? 'bg-purple-600/20 text-purple-400' 
              : event.event_type === 'showcase' 
                ? 'bg-blue-600/20 text-blue-400' 
                : 'bg-orange-600/20 text-orange-400'
          }`}>
            {event.event_type === 'all_star' ? 'All-Star' : event.event_type === 'showcase' ? 'Showcase' : 'Tournament'}
          </span>
          {!event.is_published && (
            <span className="px-2 py-0.5 bg-zinc-700 text-zinc-400 text-[10px] uppercase">Draft</span>
          )}
          {event.brackets_enabled && (
            <span className="px-2 py-0.5 bg-yellow-600/20 text-yellow-500 text-[10px] uppercase flex items-center gap-1">
              <GitBranch className="w-3 h-3" /> Brackets
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-zinc-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {event.start_date}{event.end_date && event.end_date !== event.start_date ? ` - ${event.end_date}` : ''}
          </span>
          {event.location && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {event.location}
            </span>
          )}
          <span>{event.sport}</span>
          <span>{event.broadcast_ids?.length || 0} broadcasts</span>
          <span>{event.sponsors?.length || 0} sponsors</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {event.brackets_enabled && (
          <Link 
            to={`/admin/event/${event.id}/brackets`}
            onClick={(e) => e.stopPropagation()}
            className="p-2 text-yellow-500 hover:bg-white/5"
            title="Manage Brackets"
          >
            <GitBranch className="w-4 h-4" />
          </Link>
        )}
        <button 
          onClick={(e) => { e.stopPropagation(); onTogglePublish(event); }}
          className={`p-2 ${event.is_published ? 'text-green-500' : 'text-zinc-500'} hover:bg-white/5`}
          title={event.is_published ? "Unpublish" : "Publish"}
        >
          {event.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); onEdit(event); }}
          className="p-2 text-zinc-500 hover:text-white hover:bg-white/5"
        >
          <Edit className="w-4 h-4" />
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); onDelete(event.id); }}
          className="p-2 text-zinc-500 hover:text-red-500 hover:bg-white/5"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-zinc-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-zinc-500" />
        )}
      </div>
    </div>

    {/* Expanded Details */}
    {isExpanded && (
      <div className="border-t border-white/10 p-4 space-y-4">
        {event.description && (
          <p className="text-sm text-zinc-400">{event.description}</p>
        )}

        {/* Quick Links */}
        <div className="flex items-center gap-4">
          <a 
            href={`/events/${event.slug}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            View Public Page →
          </a>
        </div>

        {/* Sponsors Preview */}
        {event.sponsors?.length > 0 && (
          <div>
            <h4 className="text-xs text-zinc-500 uppercase mb-2">Sponsors</h4>
            <div className="flex flex-wrap gap-3">
              {event.sponsors.map((sponsor, idx) => (
                <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800/50 border border-white/5">
                  {sponsor.logo_url && (
                    <img src={sponsor.logo_url} alt={sponsor.name} className="h-6 w-6 object-contain" />
                  )}
                  <span className="text-xs text-zinc-300">{sponsor.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
);

export default EventListItem;
